import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw, Send, ExternalLink, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "../ui/button";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export const SeoIndexingPanel = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/seo/index-status");
      setState(data);
    } catch {
      setState({ error: true });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/seo/submit-all");
      const inx = data?.indexnow || {};
      if (data?.ok) toast.success(`Submitted ${data.url_count} URL(s) — IndexNow: ${inx.message}`);
      else toast.error(`IndexNow: ${inx.message || "failed"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return <div className="text-xs font-mono text-[#525252]" data-testid="seo-panel-loading">Loading indexing status…</div>;
  }
  if (state.error) {
    return <div className="text-xs font-mono text-[#BE123C]" data-testid="seo-panel-error">Could not load indexing status.</div>;
  }

  return (
    <div className="border border-[#eaecf0] rounded-sm" data-testid="seo-indexing-panel">
      <div className="px-4 py-3 border-b border-[#eaecf0] bg-[#FAFAFA] flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="overline">Indexing</div>
          <div className="text-sm font-medium">Search-engine submission</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button type="button" variant="outline" className="rounded-sm text-xs" onClick={load} data-testid="seo-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Refresh
          </Button>
          <Button
            type="button"
            className="rounded-sm text-xs bg-[#03142f] hover:bg-[#0b274d] text-white"
            onClick={submitAll}
            disabled={busy}
            data-testid="seo-submit-all-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            {busy ? "Submitting…" : `Submit ${state.active_listings} landers`}
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-3 text-xs font-mono">
        <Row label="Public base" value={state.base} link={state.base} />
        <Row label="Sitemap index" value={state.sitemap} link={state.sitemap} />
        <Row label="IndexNow key file" value={state.key_location} link={state.key_location} copy={state.indexnow_key} />
        <div className="flex items-center gap-2" data-testid="seo-google-state">
          {state.google_configured
            ? <CheckCircle2 className="w-3.5 h-3.5 text-[#047857]" strokeWidth={1.5} />
            : <XCircle className="w-3.5 h-3.5 text-[#B45309]" strokeWidth={1.5} />}
          <span className="text-[#525252]">
            Google Indexing API — {state.google_configured
              ? "service account configured"
              : "not configured (IndexNow still covers Bing / Yandex / Naver)"}
          </span>
        </div>
        <div className="flex items-center gap-2" data-testid="seo-autoping-state">
          {state.auto_ping
            ? <CheckCircle2 className="w-3.5 h-3.5 text-[#047857]" strokeWidth={1.5} />
            : <XCircle className="w-3.5 h-3.5 text-[#B45309]" strokeWidth={1.5} />}
          <span className="text-[#525252]">
            Auto-submit on price / status change — {state.auto_ping ? "on" : "off (SEO_AUTO_PING=false)"}
          </span>
        </div>

        <div className="pt-2 border-t border-[#eaecf0]">
          <div className="overline mb-2">Recent submissions</div>
          {(state.recent || []).length === 0 ? (
            <div className="text-[#A1A1AA]" data-testid="seo-no-pings">No submissions yet.</div>
          ) : (
            <ul className="space-y-1.5" data-testid="seo-ping-list">
              {state.recent.map((p, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`seo-ping-${i}`}>
                  {p.indexnow?.ok
                    ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-[#047857] shrink-0" strokeWidth={1.5} />
                    : <XCircle className="w-3.5 h-3.5 mt-0.5 text-[#BE123C] shrink-0" strokeWidth={1.5} />}
                  <span className="text-[#525252] break-all">
                    {fmt(p.created_at)} · {p.reason} · {p.url_count} URL(s) · IndexNow {p.indexnow?.status} {p.indexnow?.message}
                    {p.google && !p.google.skipped ? ` · Google: ${p.google.message}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, link, copy }) => (
  <div className="flex items-start gap-2">
    <span className="text-[#A1A1AA] w-32 shrink-0">{label}</span>
    <span className="text-[#101828] break-all flex-1">{value}</span>
    {copy && (
      <button
        type="button"
        onClick={() => { navigator.clipboard?.writeText(copy); toast.success("Key copied"); }}
        className="text-[#2563EB] hover:underline shrink-0"
        data-testid={`copy-${label.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
    )}
    {link && (
      <a href={link} target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline shrink-0">
        <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
      </a>
    )}
  </div>
);

export default SeoIndexingPanel;
